/**
 * 도도매 업체 메뉴 항목 단위 라우트 가드.
 *
 * 일반/부모 매장은 childStoreMenuPaths=null이라 그대로 통과한다. 자식 매장 대표와 직원은
 * auth/me가 내려준 허용 경로만 열고, 주소를 직접 입력해도 이 경계에서 본문을 렌더하지 않는다.
 */

import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { buildChildStoreMenuCatalog } from "../../layout/AppSidebar";
import { childStoreMenuKeyForPath } from "../../../shared/child-store-menu";

export function childStoreMenuPathForRoute(pathname: string): string | null {
  const catalogPaths = buildChildStoreMenuCatalog().map(({ path }) => path);
  const key = childStoreMenuKeyForPath(pathname, catalogPaths);
  return catalogPaths.includes(key ?? "") || pathname.startsWith("/crm/") ||
    pathname.startsWith("/policy/") || pathname.startsWith("/inventory/") ||
    pathname.startsWith("/kakao-counsel") || pathname.startsWith("/store/") ||
    pathname.startsWith("/form-builder") || pathname === "/library" ||
    pathname === "/recurring-tasks"
    ? key
    : null;
}

function Notice({ empty }: { empty: boolean }) {
  return (
    <div className="flex min-h-[55vh] items-center justify-center px-4 text-center">
      <div className="max-w-xl rounded-2xl border border-gray-200 bg-white px-8 py-10 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <h1 className="text-xl font-bold text-gray-800 dark:text-white/90">
          {empty ? "아직 사용할 수 있는 메뉴가 없습니다." : "이 화면은 볼 수 없도록 설정되어 있습니다."}
        </h1>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          상위 매장 대표님께 문의해주세요.
        </p>
      </div>
    </div>
  );
}

export default function ChildStoreMenuBoundary() {
  const { pathname } = useLocation();
  const { profile } = useAuth();
  const enabledPaths = profile?.childStoreMenuPaths;

  // undefined/null은 기존 서버 또는 일반·부모 매장 — 기존 권한 흐름을 그대로 둔다.
  if (enabledPaths == null) return <Outlet />;
  if (enabledPaths.length === 0) return <Notice empty />;

  if (pathname === "/") {
    const enabled = new Set(enabledPaths);
    const first = buildChildStoreMenuCatalog().find(({ path }) => enabled.has(path));
    return first ? <Navigate to={first.path} replace /> : <Notice empty />;
  }

  const menuPath = childStoreMenuPathForRoute(pathname);
  if (menuPath && !enabledPaths.includes(menuPath)) return <Notice empty={false} />;
  return <Outlet />;
}
